import { z } from 'zod';
import { SKINFOLD_PROTOCOLS } from './clinicAnthropometry.js';
import { localDateOf } from './timezone.js';
/**
 * A linha do tempo do corpo (P3, estudo do personal: "Galileu, dobras, perimetria, peso do
 * diário e fotos num gráfico só, cada um assinando o que é seu"). Junta as medidas de todas as
 * fontes por medida (peso, gordura, músculo, cintura), cada ponto com quem mediu. Gordura de
 * métodos diferentes fica em linhas separadas: bioimpedância e cada protocolo de dobras dão
 * números que não se comparam (a mesma regra da avaliação física).
 */
export const BODY_METRICS = ['peso', 'gordura', 'musculo', 'cintura'];
export const BODY_METRIC_INFO = {
    peso: { label: 'Peso', unit: 'kg' },
    gordura: { label: 'Gordura', unit: '%' },
    musculo: { label: 'Músculo', unit: 'kg' },
    cintura: { label: 'Cintura', unit: 'cm' },
};
export const BODY_SOURCES = [
    'bioimpedancia',
    ...SKINFOLD_PROTOCOLS,
    'medidas',
    'checkin',
    'pessoa',
];
export const BODY_SOURCE_LABEL = {
    bioimpedancia: 'Bioimpedância',
    jp3: 'Dobras JP3',
    jp7: 'Dobras JP7',
    guedes: 'Dobras Guedes',
    petroski: 'Dobras Petroski',
    medidas: 'Fita métrica',
    checkin: 'Check-in da semana',
    pessoa: 'Registrado pela pessoa',
};
export const bodyPointSchema = z.object({
    id: z.string(),
    metric: z.enum(BODY_METRICS),
    value: z.number(),
    source: z.enum(BODY_SOURCES),
    /** Quem mediu ou registrou (o nome do profissional, ou "Aluno"/"Paciente"). */
    author: z.string(),
    at: z.string(),
    day: z.string(),
    /** A avaliação de onde veio (para abrir as fotos daquele dia). */
    assessmentId: z.string().nullable(),
});
export const bodyTimelineSchema = z.object({
    points: z.array(bodyPointSchema),
    /** Dias com fotos da avaliação (frente, lado, costas). */
    photoDays: z.array(z.string()),
});
const num = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;
/** Monta os pontos de todas as fontes, do mais antigo para o mais novo. */
export function bodyTimeline(input) {
    const points = [];
    const push = (base, id, metric, value) => {
        if (value === null || value <= 0)
            return;
        points.push({ ...base, id: `${id}:${metric}`, metric, value });
    };
    for (const row of input.assessments) {
        const data = row.data;
        const fromPerson = data.origem === 'paciente';
        const protocol = SKINFOLD_PROTOCOLS.includes(data.protocol)
            ? data.protocol
            : null;
        const source = row.type === 'bioimpedancia'
            ? 'bioimpedancia'
            : protocol
                ? protocol
                : fromPerson
                    ? 'pessoa'
                    : 'medidas';
        if (row.type !== 'bioimpedancia' && row.type !== 'antropometria')
            continue;
        const base = {
            source,
            author: fromPerson ? input.personLabel : (row.author ?? 'Clínica'),
            at: row.measuredAt,
            day: localDateOf(new Date(row.measuredAt), input.timeZone),
            assessmentId: row.id,
        };
        push(base, row.id, 'peso', num(data.weightKg));
        // Gordura só de método de verdade (bioimpedância ou dobras); fita métrica não mede gordura.
        if (source === 'bioimpedancia' || protocol)
            push(base, row.id, 'gordura', num(data.fatPercent));
        if (source === 'bioimpedancia')
            push(base, row.id, 'musculo', num(data.muscleKg));
        push(base, row.id, 'cintura', num(data.waistCm));
    }
    for (const row of input.checkins)
        push({
            source: 'checkin',
            author: input.personLabel,
            at: row.sentAt,
            day: localDateOf(new Date(row.sentAt), input.timeZone),
            assessmentId: null,
        }, row.id, 'peso', row.weightKg);
    points.sort((a, b) => a.at.localeCompare(b.at));
    const withPhotos = new Set(input.photoAssessmentIds ?? []);
    const photoDays = [
        ...new Set(input.assessments
            .filter((row) => withPhotos.has(row.id))
            .map((row) => localDateOf(new Date(row.measuredAt), input.timeZone))),
    ].sort();
    return { points, photoDays };
}
